import { type InternalAxiosRequestConfig, isAxiosError } from 'axios'
import type { AuthBridge } from './auth-bridge'
import { AuthActionType, classifyAuthResponse, extractErrorCode } from './auth-error'

// Injectable Axios interceptor logic (ADR-0011 identity renewal + ADR-0012 authorization outcomes). Loop-safe auth-error
// state lives in the factory closure (per instance), so tests exercise the state
// machine with mock deps — no real redirects or network.

type RetriableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean }

export interface RequestInterceptorDeps {
	getBridge: () => AuthBridge | null
}

/** Attach `Authorization: Bearer <token>` from the OIDC bridge when a valid access token exists. */
export function createAuthRequestInterceptor(deps: RequestInterceptorDeps) {
	return (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
		const token = deps.getBridge()?.getAccessToken()
		if (token) config.headers.Authorization = `Bearer ${token}`
		return config
	}
}

export interface ResponseInterceptorDeps {
	getBridge: () => AuthBridge | null
	/** Replay a request (the Axios instance) — kept injectable for tests. */
	replay: (config: InternalAxiosRequestConfig) => Promise<unknown>
}

/**
 * Response error interceptor implementing the typed 401/403 state machine:
 * - `401` → one shared in-flight renewal; on success, replay the ORIGINAL request
 *   exactly once (marked so a replay cannot re-enter renewal); on failure/second
 *   401 → clear session + at most one login redirect.
 * - `403 USER_INACTIVE` → clear session + inactive state.
 * - `403 ACCESS_FORBIDDEN` / `USER_NOT_PROVISIONED` → preserve session, set typed state.
 * - anything else (network / 5xx / unknown 403) → reject unchanged; no login.
 */
export function createAuthResponseInterceptor(deps: ResponseInterceptorDeps) {
	let renewalPromise: Promise<string | null> | null = null // shared across concurrent 401s
	let redirectStarted = false // at most one redirect

	const renewOnce = (): Promise<string | null> => {
		if (!renewalPromise) {
			const bridge = deps.getBridge()
			renewalPromise = (bridge ? bridge.renew() : Promise.resolve(null)).finally(() => {
				renewalPromise = null
			})
		}
		return renewalPromise
	}

	const clearAndRedirect = async (): Promise<void> => {
		const bridge = deps.getBridge()
		await bridge?.clearSession()
		if (redirectStarted) return
		redirectStarted = true
		bridge?.startLogin()
	}

	return async (error: unknown): Promise<unknown> => {
		if (!isAxiosError(error)) return Promise.reject(error)
		const action = classifyAuthResponse(error.response?.status, extractErrorCode(error.response?.data))

		if (action.type === AuthActionType.RENEW) {
			const original = error.config as RetriableConfig | undefined
			if (!original || original._authRetried) {
				await clearAndRedirect()
				return Promise.reject(error)
			}
			const token = await renewOnce()
			if (!token) {
				await clearAndRedirect()
				return Promise.reject(error)
			}
			original._authRetried = true // cannot re-enter renewal
			original.headers.Authorization = `Bearer ${token}`
			return deps.replay(original)
		}

		if (action.type === AuthActionType.CLEAR_SESSION) {
			const bridge = deps.getBridge()
			await bridge?.clearSession()
			bridge?.setAuthErrorState(action.state)
			return Promise.reject(error)
		}

		if (action.type === AuthActionType.ERROR_STATE) {
			deps.getBridge()?.setAuthErrorState(action.state) // session preserved
			return Promise.reject(error)
		}

		return Promise.reject(error)
	}
}
